import { FC, useEffect, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { useParams } from 'react-router';

import { ServiceType } from '@/shared/api/protocol-ts/api/billing_new/dto_services_new_pb';
import { FeatureFlag } from '@/shared/api/protocol-ts/model/dto_organization_pb';
import sadCatImg from '@/shared/assets/images/sad-cat.png';
import { useAppDispatch, useAppSelector, useMedia } from '@/shared/hooks';
import { trackEvent } from '@/shared/lib';
import { Button, Layout, MobileLayout, Result, Spinner } from '@/shared/ui';

import { albumModel } from '@/entities/album';
import { billingModel } from '@/entities/billing';
import { composedModel } from '@/entities/composed';
import { ModalID, modalModel } from '@/entities/modal';
import { organizationModel } from '@/entities/organization';
import { patientModel } from '@/entities/patient';

import { Footer } from '@/features/footer';
import { useUploadAssetContext } from '@/features/uploadAsset';
import {
  MobileDentalPhotoUploadMenu,
  UploadDentalPhotosModal,
} from '@/features/uploadDentalPhotos/';

import { PhotoSlider } from '@/widgets/DentalPhotoSlider';
import { EditAlbumModal } from '@/widgets/EditAlbumModal';
import { Header, MobileHeader } from '@/widgets/Header';

import { Album, DentalPhotosHeader } from '@/pages/DentalPhotos/ui';

import styles from './DentalPhotos.module.scss';

export const DentalPhotos: FC = () => {
  const { formatMessage } = useIntl();
  const { patientID = '' } = useParams<{ patientID: string }>();
  const dispatch = useAppDispatch();
  const { isMobile } = useMedia();

  const { CanUploadDentalPhoto, CanEditAlbums } = useAppSelector(
    patientModel.selectors.selectPatientYourPermissions(patientID),
  );

  const isUploadServiceActive = useAppSelector(
    billingModel.selectors.selectIsServiceActiveByType(
      ServiceType.ServiceType_Enable_UploadDentalPhoto,
    ),
  );
  const isStorageBlocked = useAppSelector(
    composedModel.selectors.selectIsStorageBlocked,
  );
  const isForbidUploadsFromWeb = useAppSelector(
    organizationModel.selectors.selectFeatureFlag(
      FeatureFlag.FeatureFlag_Forbid_UploadsFromWeb,
    ),
  );

  const canUpload = CanUploadDentalPhoto && isUploadServiceActive;
  const dentalPhotoAlbums = useAppSelector(albumModel.selectors.selectAll);

  const loading = useAppSelector(albumModel.selectors.selectLoading);

  const { uploadSessionPullStack } = useUploadAssetContext();

  const [pendingUpload, setPendingUpload] = useState<{
    albumID?: string;
  } | null>(null);
  const albumCountRef = useRef(dentalPhotoAlbums.length);
  const albumAssetCountRef = useRef(
    dentalPhotoAlbums.reduce((sum, a) => sum + a.StudyAssetIDs.length, 0),
  );

  // Set pending when upload succeeds
  useEffect(() => {
    const session = Object.values(uploadSessionPullStack).find(
      (s) => s.patientID === patientID && s.status === 'success',
    );

    if (session && !pendingUpload) {
      albumCountRef.current = dentalPhotoAlbums.length;
      albumAssetCountRef.current = dentalPhotoAlbums.reduce(
        (sum, a) => sum + a.StudyAssetIDs.length,
        0,
      );
      setPendingUpload({
        albumID: (session.meta?.albumID as string) ?? undefined,
      });
    }
  }, [uploadSessionPullStack, patientID]);

  // Clear pending when albums actually update
  useEffect(() => {
    if (!pendingUpload) {
      return;
    }

    const currentAssetCount = dentalPhotoAlbums.reduce(
      (sum, a) => sum + a.StudyAssetIDs.length,
      0,
    );
    const albumsChanged = dentalPhotoAlbums.length !== albumCountRef.current;
    const assetsChanged = currentAssetCount !== albumAssetCountRef.current;

    if (albumsChanged || assetsChanged) {
      setPendingUpload(null);
    }
  }, [dentalPhotoAlbums, pendingUpload]);

  const isProcessingNewAlbum = pendingUpload !== null && !pendingUpload.albumID;
  const processingAlbumID = pendingUpload?.albumID;

  const handleUploadClick = () => {
    dispatch(
      modalModel.actions.openModal({
        modalID: ModalID.DentalPhotosUpload,
      }),
    );
  };

  const handleMobileUploadClick = () => {
    dispatch(
      modalModel.actions.openModal({
        modalID: ModalID.MobileDentalPhotoUploadMenu,
        data: { patientID },
      }),
    );
  };

  const modals = (
    <>
      {!isMobile && patientID && (
        <UploadDentalPhotosModal patientID={patientID} />
      )}
      {isMobile && <MobileDentalPhotoUploadMenu />}
      {dentalPhotoAlbums.length > 0 && <EditAlbumModal />}
      <PhotoSlider />
    </>
  );

  const emptyStateContent = (
    <Result
      className={styles.emptyResult}
      icon={
        <img
          src={sadCatImg}
          width={130}
          height={130}
          alt={formatMessage({
            id: 'imgAltText.sadCat',
            defaultMessage: 'Sad cat',
          })}
        />
      }
      text={formatMessage({
        id: 'dentalPhotosPage.emptyResult.text',
        defaultMessage: 'There are no dental photos yet',
      })}
    />
  );

  useEffect(() => {
    trackEvent({
      eventName: 'DentalPhotosPageViewed',
      properties: { patient_id: patientID },
      isPageViewEvent: true,
    });
  }, [patientID]);

  const newAlbumSkeleton = isProcessingNewAlbum && (
    <div className={styles.skeleton}>
      <div className={styles.skeletonHeader}>
        <div className={styles.skeletonLine} />
      </div>

      <div className={styles.skeletonPhotos}>
        {Array.from({ length: 3 }).map((_, index) => (
          <div key={index} className={styles.skeletonPhoto} />
        ))}
      </div>
    </div>
  );

  if (loading === 'pending') {
    return (
      <div className={styles.spinnerWrapper}>
        <Spinner className={styles.spinner} size={96} />
      </div>
    );
  }

  if (isMobile) {
    return (
      <>
        <MobileLayout>
          <MobileHeader hideBreadcrumbs />

          <MobileLayout.Main>
            <DentalPhotosHeader
              canUpload={canUpload}
              isMobile
              isStorageBlocked={isStorageBlocked}
              uploadControl={
                <Button
                  data-component="upload-dental-photos-button"
                  variant="secondary"
                  size="medium"
                  disabled={isStorageBlocked || isForbidUploadsFromWeb}
                  onClick={handleMobileUploadClick}
                >
                  {formatMessage({
                    id: 'global.upload',
                    defaultMessage: 'Upload',
                  })}
                </Button>
              }
              onUploadClick={handleUploadClick}
            />

            {newAlbumSkeleton}

            {dentalPhotoAlbums?.length > 0
              ? dentalPhotoAlbums.map((album) => (
                  <Album
                    key={album.ID}
                    album={album}
                    canEditAlbums={CanEditAlbums}
                    canUpload={canUpload}
                    isStorageBlocked={isStorageBlocked}
                    patientID={patientID}
                    isProcessingUpload={processingAlbumID === album.ID}
                  />
                ))
              : !isProcessingNewAlbum && emptyStateContent}
          </MobileLayout.Main>
        </MobileLayout>
        {modals}
      </>
    );
  }

  return (
    <>
      <Layout>
        <Layout.Header>
          <Header />
        </Layout.Header>

        <Layout.Content>
          <Layout.Main>
            <div className={styles.container} data-section="dental-photos">
              <DentalPhotosHeader
                canUpload={canUpload}
                isStorageBlocked={isStorageBlocked}
                onUploadClick={handleUploadClick}
              />

              <div className={styles.innerContainer}>
                {newAlbumSkeleton}

                {dentalPhotoAlbums?.length > 0 &&
                  dentalPhotoAlbums.map((album) => (
                    <Album
                      key={album.ID}
                      album={album}
                      canEditAlbums={CanEditAlbums}
                      canUpload={canUpload}
                      isStorageBlocked={isStorageBlocked}
                      patientID={patientID}
                      isProcessingUpload={processingAlbumID === album.ID}
                    />
                  ))}
              </div>

              {dentalPhotoAlbums.length === 0 && !isProcessingNewAlbum && (
                <div className={styles.emptyResult}>{emptyStateContent}</div>
              )}
            </div>
          </Layout.Main>
        </Layout.Content>

        <Layout.Footer>
          <Footer />
        </Layout.Footer>
      </Layout>
      {modals}
    </>
  );
};
